// retention.ts
// Pure helpers for source-file retention: when a file's bytes get deleted,
// whether it is kept (pinned), and the 7-day "about to be deleted" warning.
//
// Authority is `expiresAt` on the ELEMENT rows (docs/STORAGE_RETENTION_PLAN.md,
// W0). A row without `expiresAt` is NOT scheduled for deletion (legacy data
// until backfilled): never warn about it. Do not fall back to createdAt age
// math — that is what produced false "pin to keep" prompts for legacy files
// that were never going to be deleted.
//
// Constants mirror infrastructure/resources/libs/retention.ts (the backend
// contract). The UI does not import infra code; keep both in sync.
export const RETENTION_DAYS = 30;
export const KEEP_DAYS = 365;
/** Minimum notice before an unpinned file can be deleted. */
export const RETENTION_GRACE_DAYS = 7;
/** How long before `expiresAt` the UI starts warning. */
export const RETENTION_WARN_DAYS = 7;
export const RETENTION_DELETED_STATUS = 'file-deleted';

const DAY_MS = 86_400_000;

export interface RetentionRow {
  PK?: string;
  SK?: string;
  fileName?: string;
  status?: string;
  createdAt?: string;
  expiresAt?: string | null;
  keepForever?: boolean;
}

/** Kept for API compatibility with earlier callers. */
export type RetentionInput = RetentionRow;
export type KeepFlag = RetentionRow;

function toMs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Transcript elements (OUTPUT# rows with a transcript extension) are never
 * scheduled for deletion. Mirrors isTranscriptElement in
 * infrastructure/resources/libs/transcript-catalog.ts.
 */
export function isTranscriptElement(row: RetentionRow | null | undefined): boolean {
  if (!row) return false;
  return String(row.PK || '').startsWith('OUTPUT#') && /\.(json|srt|vtt|txt)$/i.test(String(row.fileName || ''));
}

export function isFileDeletedRow(row: RetentionRow | null | undefined): boolean {
  return !!row && row.status === RETENTION_DELETED_STATUS;
}

/** Row-level expiry (ms) or null when the row is not scheduled. */
export function rowExpiresAtMs(row: RetentionRow | null | undefined): number | null {
  if (!row || isTranscriptElement(row) || isFileDeletedRow(row)) return null;
  return toMs(row.expiresAt);
}

/** True when the row is inside its final warning window before deletion. */
export function isExpiringSoon(row: RetentionRow | null | undefined, nowMs: number = Date.now()): boolean {
  const exp = rowExpiresAtMs(row);
  if (exp === null) return false;
  return exp - nowMs <= RETENTION_WARN_DAYS * DAY_MS;
}

export type FileRetentionState = 'none' | 'scheduled' | 'kept' | 'deleted';

export interface FileRetention {
  state: FileRetentionState;
  /** Latest expiresAt among the file's live source elements (ms). */
  expiresAtMs: number | null;
  /** Scheduled or kept, and inside the warning window. */
  expiringSoon: boolean;
  /** The file still has transcript elements (shown after the source is gone). */
  hasTranscripts: boolean;
}

/**
 * File-level retention from the input row and all its element rows (input
 * elements + every output's elements).
 * - Source elements = non-transcript elements. When all are 'file-deleted',
 *   the file is 'deleted'.
 * - Expiry = the LATEST expiresAt among live source elements (the file is
 *   only fully gone when the last one goes). None dated → 'none'.
 * - Kept = every live source element is keepForever.
 * - Without any element rows (e.g. pending upload), the input row's mirrored
 *   fields are used for display.
 */
export function fileRetention(
  input: RetentionRow | null | undefined,
  elements: RetentionRow[] | null | undefined,
  nowMs: number = Date.now()
): FileRetention {
  const none: FileRetention = { state: 'none', expiresAtMs: null, expiringSoon: false, hasTranscripts: false };
  if (!input) return none;
  const els = (elements || []).filter(Boolean);
  const hasTranscripts = els.some(isTranscriptElement);
  const warn = (ms: number) => ms - nowMs <= RETENTION_WARN_DAYS * DAY_MS;

  if (els.length === 0) {
    if (isFileDeletedRow(input)) return { ...none, state: 'deleted' };
    const ms = toMs(input.expiresAt);
    if (ms === null) return none;
    return { state: input.keepForever ? 'kept' : 'scheduled', expiresAtMs: ms, expiringSoon: warn(ms), hasTranscripts };
  }

  const sources = els.filter((e) => !isTranscriptElement(e));
  if (sources.length === 0) return { ...none, hasTranscripts };
  const live = sources.filter((e) => !isFileDeletedRow(e));
  if (live.length === 0) return { ...none, state: 'deleted', hasTranscripts };

  let latest: number | null = null;
  for (const e of live) {
    const ms = toMs(e.expiresAt);
    if (ms !== null && (latest === null || ms > latest)) latest = ms;
  }
  if (latest === null) return { ...none, hasTranscripts };
  const kept = live.every((e) => !!e.keepForever);
  return { state: kept ? 'kept' : 'scheduled', expiresAtMs: latest, expiringSoon: warn(latest), hasTranscripts };
}

/**
 * File-level kept state: every live source element is pinned. A file with
 * no elements falls back to the input row's flag.
 */
export function isFileKept(input: RetentionRow | null | undefined, elements: RetentionRow[] | null | undefined): boolean {
  if (!input) return false;
  const els = (elements || []).filter(Boolean);
  if (els.length === 0) return !!input.keepForever;
  const live = els.filter((e) => !isTranscriptElement(e) && !isFileDeletedRow(e));
  return live.length > 0 && live.every((e) => !!e.keepForever);
}

/** Files inside their warning window (kept files whose year is ending included). */
export function expiringInputs<T extends RetentionRow>(
  all: T[],
  getElements: (input: T) => RetentionRow[] = () => [],
  nowMs: number = Date.now()
): T[] {
  return (all || []).filter((i) => fileRetention(i, getElements(i), nowMs).expiringSoon);
}

/** Alias kept for earlier callers: expiring files that pinning would save. */
export const unkeptExpiring = expiringInputs;

/**
 * Elements a "keep" action should (re)pin: live source elements that are
 * not pinned yet, or whose pin is about to lapse. Transcripts never expire,
 * so they are skipped.
 */
export function elementsToKeep<T extends RetentionRow>(elements: T[] | null | undefined, nowMs: number = Date.now()): T[] {
  return (elements || []).filter(
    (e) => !!e && !isTranscriptElement(e) && !isFileDeletedRow(e) && (!e.keepForever || isExpiringSoon(e, nowMs))
  );
}

// vue-i18n locale codes → BCP 47 for Intl (cn is not a region-less tag).
const LOCALE_MAP: Record<string, string> = { cn: 'zh-CN' };

/** Localized calendar date for retention labels ("12 Oct 2026"). */
export function formatRetentionDate(ms: number | null | undefined, locale?: string): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return '';
  const tag = (locale && LOCALE_MAP[locale]) || locale || undefined;
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
  try {
    return new Date(ms).toLocaleDateString(tag, opts);
  } catch {
    return new Date(ms).toLocaleDateString(undefined, opts);
  }
}

/** Short relative age for "updated 3 hours ago" (Intl.RelativeTimeFormat). */
export function formatRelativeTime(iso: string | null | undefined, locale?: string, nowMs: number = Date.now()): string {
  const ms = toMs(iso);
  if (ms === null) return '';
  const tag = (locale && LOCALE_MAP[locale]) || locale || undefined;
  const diffSec = Math.round((ms - nowMs) / 1000);
  const abs = Math.abs(diffSec);
  const [value, unit]: [number, Intl.RelativeTimeFormatUnit] =
    abs < 60 ? [diffSec, 'second']
      : abs < 3600 ? [Math.round(diffSec / 60), 'minute']
        : abs < 86400 ? [Math.round(diffSec / 3600), 'hour']
          : abs < 30 * 86400 ? [Math.round(diffSec / 86400), 'day']
            : abs < 365 * 86400 ? [Math.round(diffSec / (30 * 86400)), 'month']
              : [Math.round(diffSec / (365 * 86400)), 'year'];
  try {
    return new Intl.RelativeTimeFormat(tag, { numeric: 'auto' }).format(value, unit);
  } catch {
    return new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }).format(value, unit);
  }
}

/** Human-readable byte size ("1.5 GB"), binary units (matches the S3 recount). */
export function formatBytes(bytes: number | null | undefined): string {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const n = Number(bytes);
  if (!n || !Number.isFinite(n) || n <= 0) return '0 Bytes';
  const i = Math.min(sizes.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  return parseFloat((n / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
}
